import { useHandler } from "@redotech/react-util/hook";
import ChevronDownSvg from "@redotech/redo-icons/chevron-down_filled.svg";
import ChevronUpSvg from "@redotech/redo-icons/chevron-up_filled.svg";
import HelpIcon from "@redotech/redo-icons/help-circle.svg";
import classNames from "classnames";
import {
  ChangeEventHandler,
  ForwardedRef,
  forwardRef,
  JSXElementConstructor,
  KeyboardEventHandler,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { SpacingValue } from "../../theme/box";
import { TextSizeValue } from "../../theme/typography";
import { Tooltip } from "../../tooltip/tooltip";
import * as css from "./redo-increment-decrement.module.css";
import { snapDecrement, snapIncrement } from "./snap-step";

export const incrementDecrementSizes = ["regular", "small"] as const;
export type IncrementDecrementSize = (typeof incrementDecrementSizes)[number];

/**
 * @param required -- when used in a form, will prevent the form from submitting if the input is empty.
 *
 * @param dangerousStyleThatShouldOnlyBeUsedForMerchantBranding -- this prop should only be defined
 * when we want the input to follow a merchant's branding. Do not use it to style this component for a Redo use case.
 */
export interface RedoIncrementDecrementProps {
  ariaLabel?: string;
  autoFocus?: boolean;
  className?: string;
  dataTestId?: string;
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
  decimalPlaces?: number;
  description?: string;
  disabled?: boolean;
  error?: boolean;
  warning?: boolean;
  fullWidth?: boolean;
  IconLeading?: JSXElementConstructor<any>;
  label?: string;
  /** Help-circle icon + tooltip shown next to the label. */
  infoTooltip?: string;
  max?: number;
  min?: number;
  /**
   * Minimum number of digits to display. Numbers will be padded with leading zeros.
   * For example, minimumDisplayDigits=2 will display "05" instead of "5".
   */
  minimumDisplayDigits?: number;
  name?: string;
  /**
   * @param text text of the input when it loses focus
   */
  onBlur?(text: string): void;
  placeholder?: string;
  prefix?: string;
  readonly?: boolean;
  required?: boolean;
  selectOnFocus?: boolean;
  setValue(value: number): void;
  size?: IncrementDecrementSize;
  step?: number | "any";
  stopClickPropagation?: boolean;
  suffix?: string;
  /**
   * Number, or undefined if blank.
   * If the placeholder is numeric, it will be used instead of undefined.
   */
  value: number | undefined;
  uiSelector?: string;
  /**
   * When set, the input is sized to fit this sample string (e.g. "100") and
   * stays that width regardless of the current value. Use it to keep adjacent
   * controls from shifting as the user edits.
   */
  widthHint?: string;
}

const MAX_PRECISION = 6;

export const RedoIncrementDecrement = memo(
  forwardRef(function RedoIncrementDecrement(
    {
      ariaLabel,
      autoFocus,
      className,
      dataTestId,
      dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
      decimalPlaces,
      description,
      disabled,
      error,
      warning,
      fullWidth,
      IconLeading,
      label,
      infoTooltip,
      max,
      min,
      minimumDisplayDigits,
      name,
      onBlur,
      placeholder,
      prefix,
      readonly,
      required,
      selectOnFocus = true,
      setValue,
      size = "regular",
      step,
      stopClickPropagation,
      suffix,
      value,
      uiSelector,
      widthHint,
    }: RedoIncrementDecrementProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) {
    const parse = (text: string): number =>
      text ? +text : !isNaN(+placeholder!) ? +placeholder! : 0;

    const stringify = useCallback(
      (value: number | undefined) => {
        if (value === undefined) {
          return "";
        }

        // Use fixed decimal places if specified (e.g., for currency)
        if (decimalPlaces !== undefined) {
          return value.toFixed(decimalPlaces);
        }

        // limit length for floating point math, e.g. increment 0.1 thrice
        const formatted =
          value.toFixed(MAX_PRECISION).replace(/\.?0*$/, "") || "0";

        // Apply minimum display digits padding if specified and value is a whole number
        if (minimumDisplayDigits && Number.isInteger(value)) {
          return formatted.padStart(minimumDisplayDigits, "0");
        }

        return formatted;
      },
      [decimalPlaces, minimumDisplayDigits],
    );

    const descriptorFontSize = sizeToDescriptorTextProps[size];
    // Match base-redo-input's info icon: 12px small, 16px otherwise.
    const infoIconSize = size === "small" ? 12 : 16;
    const stepSize = step === "any" ? 1 : (step ?? 1);

    const hiddenRef = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const incrementRef = useRef<HTMLButtonElement>(null);
    const decrementRef = useRef<HTMLButtonElement>(null);
    const wrapperRef = useRef<HTMLElement>(null);
    const [text, setText] = useState(stringify(value));
    const [width, setWidth] = useState<number | undefined>();

    useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
      ref,
      () => inputRef.current,
    );

    const clamp = (value: number) =>
      Math.min(Math.max(value, min ?? -Infinity), max ?? Infinity);

    useLayoutEffect(() => {
      // set the input value, if not currently being edited
      if (wrapperRef.current?.contains(document.activeElement)) {
        return;
      }
      setText(stringify(value));
    }, [value, stringify]);

    useEffect(() => {
      // set the input width using ResizeObserver so it fires when the element
      // is actually laid out (e.g. after a modal finishes animating in)
      if (!hiddenRef.current) {
        return;
      }
      const observer = new ResizeObserver(() => {
        const { width } = hiddenRef.current!.getBoundingClientRect();
        if (width > 0) {
          setWidth(width);
        }
      });
      observer.observe(hiddenRef.current);
      return () => observer.disconnect();
    }, [text]);

    const handleBlur = useHandler(() => {
      onBlur?.(inputRef.current!.value);
      setText(stringify(value));
    });

    const handleChange: ChangeEventHandler<HTMLInputElement> = useHandler(
      (event) => {
        const text = event.target.value;
        setText(text);
        setValue(clamp(parse(text)));
      },
    );

    const handleFocus = useHandler(() => {
      if (selectOnFocus) {
        inputRef.current?.select();
      }
    });

    const handleIncrement = useHandler(() => {
      const value = clamp(snapIncrement(parse(text) ?? 0, stepSize));
      setText(stringify(value));
      setValue(value);
    });

    const handleDecrement = useHandler(() => {
      const value = clamp(snapDecrement(parse(text) ?? 0, stepSize));
      setText(stringify(value));
      setValue(value);
    });

    const handleKeyDown: KeyboardEventHandler = useHandler((e) => {
      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          incrementRef.current?.focus();
          handleIncrement();
          break;
        case "ArrowDown":
          e.preventDefault();
          decrementRef.current?.focus();
          handleDecrement();
          break;
        case "Backspace":
          if (inputRef.current && document.activeElement !== inputRef.current) {
            inputRef.current.focus();
            const text = inputRef.current.value.slice(0, -1);
            setText(text);
            setValue(parse(text));
          }
          break;
      }
    });

    return (
      <Flex
        data-arbiter="RedoIncrementDecrement"
        data-ui={uiSelector}
        dir="column"
        gap="sm"
        onClick={stopClickPropagation ? (e) => e.stopPropagation() : undefined}
        w={fullWidth ? "full" : "fit-content"}
      >
        {label && (
          <Flex align="center" gap="xs">
            <Text fontSize={descriptorFontSize}>{label}</Text>
            {infoTooltip && (
              <Tooltip title={infoTooltip}>
                <Flex align="center" textColor="quinary">
                  <HelpIcon
                    style={{ width: infoIconSize, height: infoIconSize }}
                  />
                </Flex>
              </Tooltip>
            )}
          </Flex>
        )}
        <Flex
          align="center"
          as="label"
          borderRadius={size === "small" ? "sm" : "lg"}
          className={classNames(
            disabled && css.disabled,
            css.inputWrapper,
            error && css.error,
            warning && !error && css.warning,
            className,
            css[size],
          )}
          fontSize={size === "small" ? "xs" : "md"}
          gap="xs"
          justify="space-between"
          pl={sizeToLeftPadding[size]}
          pr="none"
          py={sizeToVerticalPadding[size]}
          ref={wrapperRef}
          style={dangerousStyleThatShouldOnlyBeUsedForMerchantBranding}
          w={fullWidth ? "full" : "fit-content"}
        >
          <Flex align="center" gap="xs">
            {IconLeading && (
              <Flex className={css.leadingIcon} mr="xs">
                <IconLeading />
              </Flex>
            )}
            {prefix ? <Text>{prefix}</Text> : null}
            <span className={css.hiddenText} ref={hiddenRef}>
              {widthHint ?? (text || placeholder)}
            </span>
            <input
              aria-label={ariaLabel}
              autoFocus={autoFocus}
              className={classNames(css.input, css[size])}
              data-testid={dataTestId}
              disabled={disabled}
              name={name}
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              readOnly={readonly}
              ref={inputRef}
              required={required}
              step={step}
              style={{
                width: width !== undefined ? `${width + 4}px` : undefined,
              }}
              type="number"
              value={text}
            />
            {suffix ? <Text>{suffix}</Text> : null}
          </Flex>
          <Flex
            align="flex-end"
            className={css.arrows}
            dir="column"
            gap="none"
            justify="space-between"
          >
            <button
              className={css.incrementer}
              disabled={
                readonly ||
                disabled ||
                clamp(snapIncrement(value || 0, stepSize)) === value
              }
              onClick={handleIncrement}
              onKeyDown={handleKeyDown}
              ref={incrementRef}
              tabIndex={-1}
              type="button"
            >
              <ChevronUpSvg />
            </button>
            <button
              className={css.decrementer}
              disabled={
                readonly ||
                disabled ||
                clamp(snapDecrement(value || 0, stepSize)) === value
              }
              onClick={handleDecrement}
              onKeyDown={handleKeyDown}
              ref={decrementRef}
              tabIndex={-1}
              type="button"
            >
              <ChevronDownSvg />
            </button>
          </Flex>
        </Flex>
        {description && (
          <Text
            fontSize={descriptorFontSize}
            textColor={error ? "error" : "tertiary"}
          >
            {description}
          </Text>
        )}
      </Flex>
    );
  }),
);

const sizeToVerticalPadding: Record<IncrementDecrementSize, SpacingValue> = {
  regular: "md",
  small: "xxs",
};

const sizeToLeftPadding: Record<IncrementDecrementSize, SpacingValue> = {
  regular: "none", // 14px in CSS module (not an arbiter spacing value)
  small: "md",
};

const sizeToDescriptorTextProps: Record<IncrementDecrementSize, TextSizeValue> =
  { regular: "sm", small: "xs" };
